import Dexie from 'dexie'

export const db = new Dexie('ClystcoreSurveyors')

// Version 10: Force clear all photos to fix phantom photo issue
db.version(10).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, propertyType, latitude, longitude, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, remoteId, analyseWithAI, capturedAt, uploaded',
  settings: 'key',
  activity_logs: '++id, surveyId, type, timestamp, propertyName, surveyLevel'
}).upgrade(async tx => {
  // Clear all photos to fix phantom photo issue
  await tx.table('photos').clear()
  console.log('[Database v10] Cleared all photos')
})

// Version 9: Add activity_logs table for tracking PDF downloads
db.version(9).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, propertyType, latitude, longitude, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, remoteId, analyseWithAI, capturedAt, uploaded',
  settings: 'key',
  activity_logs: '++id, surveyId, type, timestamp, propertyName, surveyLevel'
})

// Version 8: Add remoteId index to photos table
db.version(8).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, propertyType, latitude, longitude, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, remoteId, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
})

// Version 7: Clean migration - reload from API
db.version(7).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, propertyType, latitude, longitude, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(async tx => {
  // Clear all surveys - they will be reloaded from API
  await tx.table('surveys').clear()
  await tx.table('photos').clear()
  console.log('Database upgraded to v7 - surveys will reload from API')
})

// Version 6: Remove clientName field (reverted)
db.version(6).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, propertyType, latitude, longitude, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(tx => {
  // Migration: Remove clientName field if it exists
  return tx.table('surveys').toCollection().modify(survey => {
    if (survey.clientName !== undefined) {
      delete survey.clientName
    }
  })
})

// Version 5: Added clientName field (REVERTED - kept for migration path)
db.version(5).stores({
  surveys: '++id, propertyName, address, postcode, clientName, surveyorName, surveyLevel, status, synced, remoteId, propertyType, latitude, longitude, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(tx => {
  // Migration: Add clientName field to existing surveys
  return tx.table('surveys').toCollection().modify(survey => {
    if (survey.clientName === undefined) survey.clientName = null
  })
})

// Version 4: Added property details and map coordinates
db.version(4).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, propertyType, latitude, longitude, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(tx => {
  // Migration: Add new property detail fields to existing surveys
  return tx.table('surveys').toCollection().modify(survey => {
    // Initialize new fields with null/defaults
    if (survey.propertyType === undefined) survey.propertyType = null
    if (survey.propertyAge === undefined) survey.propertyAge = null
    if (survey.bedrooms === undefined) survey.bedrooms = null
    if (survey.bathrooms === undefined) survey.bathrooms = null
    if (survey.floorAreaSqm === undefined) survey.floorAreaSqm = null
    if (survey.tenure === undefined) survey.tenure = null
    if (survey.listedStatus === undefined) survey.listedStatus = null
    if (survey.conservationArea === undefined) survey.conservationArea = false
    if (survey.parking === undefined) survey.parking = null
    if (survey.garden === undefined) survey.garden = null
    if (survey.latitude === undefined) survey.latitude = null
    if (survey.longitude === undefined) survey.longitude = null
    if (survey.geocodedAddress === undefined) survey.geocodedAddress = null
    if (survey.propertyResearch === undefined) survey.propertyResearch = null
    if (survey.researchCompletedAt === undefined) survey.researchCompletedAt = null
  })
})

// Version 3: Added synced/remoteId fields for offline-first surveys
db.version(3).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, synced, remoteId, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(tx => {
  // Migration: Add synced and remoteId fields to existing surveys
  return tx.table('surveys').toCollection().modify(survey => {
    if (survey.synced === undefined) {
      survey.synced = true  // Assume existing surveys are synced
      survey.remoteId = survey.id  // Use current id as remoteId
    }
  })
})

// Version 2: Changed categoryId to categoryCode, removed categories table
// Categories are now static config in src/config/categories.js
db.version(2).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, createdAt, updatedAt',
  photos: '++id, surveyId, categoryCode, analyseWithAI, capturedAt, uploaded',
  settings: 'key'
}).upgrade(tx => {
  // Migration: Convert old categoryId to categoryCode
  // This maps numeric IDs to category codes based on the old order
  const idToCode = {
    1: 'D1', 2: 'D2', 3: 'D3', 4: 'D4', 5: 'D5', 6: 'D6', 7: 'D7', 8: 'D8', 9: 'D9',
    10: 'E1', 11: 'E2', 12: 'E3', 13: 'E4', 14: 'E5', 15: 'E6', 16: 'E7', 17: 'E8', 18: 'E9',
    19: 'F1', 20: 'F2', 21: 'F3', 22: 'F4', 23: 'F5', 24: 'F6', 25: 'F7',
    26: 'G1', 27: 'G2', 28: 'G3'
  }

  return tx.table('photos').toCollection().modify(photo => {
    if (photo.categoryId && !photo.categoryCode) {
      photo.categoryCode = idToCode[photo.categoryId] || 'D1'
      delete photo.categoryId
    }
  })
})

// Keep version 1 for existing databases that haven't migrated yet
db.version(1).stores({
  surveys: '++id, propertyName, address, postcode, surveyorName, surveyLevel, status, createdAt, updatedAt',
  photos: '++id, surveyId, categoryId, analyseWithAI, capturedAt, uploaded',
  categories: '++id, code, section, name, order, isActive',
  settings: 'key'
})

// Initialize default settings (categories no longer needed - they're static config)
export async function initializeDatabase() {
  // Initialize default settings
  const surveyorSetting = await db.settings.get('defaultSurveyor')
  if (!surveyorSetting) {
    await db.settings.put({ key: 'defaultSurveyor', value: 'Chris I Baker' })
  }

  const apiKeySetting = await db.settings.get('openaiApiKey')
  if (!apiKeySetting) {
    await db.settings.put({ key: 'openaiApiKey', value: '' })
  }

  // Clean up orphaned photos (photos for surveys that no longer exist)
  await cleanupOrphanedPhotos()
}

// Clean up photos that belong to non-existent surveys
async function cleanupOrphanedPhotos() {
  try {
    // Get all photos
    const allPhotos = await db.photos.toArray()
    if (allPhotos.length === 0) return

    // Get all survey IDs (both local and remote)
    const surveys = await db.surveys.toArray()
    const validSurveyIds = new Set(surveys.map(s => s.id))
    const validRemoteIds = new Set(surveys.map(s => s.remoteId).filter(Boolean))

    // Find orphaned photos (photos that don't match any survey ID or remoteId)
    const orphanedPhotos = allPhotos.filter(photo => {
      const hasMatchingLocalId = validSurveyIds.has(photo.surveyId)
      const hasMatchingRemoteId = validRemoteIds.has(photo.surveyId)
      return !hasMatchingLocalId && !hasMatchingRemoteId
    })

    if (orphanedPhotos.length > 0) {
      console.log(`[Database] Found ${orphanedPhotos.length} orphaned photos, cleaning up...`)
      console.log('[Database] Orphaned photo survey IDs:', orphanedPhotos.map(p => p.surveyId))

      // Delete orphaned photos
      const orphanedIds = orphanedPhotos.map(p => p.id)
      await db.photos.bulkDelete(orphanedIds)

      console.log(`[Database] Cleaned up ${orphanedPhotos.length} orphaned photos`)
    }
  } catch (error) {
    console.error('[Database] Failed to cleanup orphaned photos:', error)
  }
}

// Helper functions for surveys
export const surveyDb = {
  async create(surveyData) {
    const now = new Date().toISOString()
    const survey = {
      ...surveyData,
      surveyLevel: surveyData.surveyLevel || 1,
      status: surveyData.status || 'in-progress',
      // Use passed values if provided, otherwise default to unsynced
      synced: surveyData.synced ?? false,
      remoteId: surveyData.remoteId ?? null,
      // NEW: Property detail fields
      propertyType: surveyData.propertyType || null,
      propertyAge: surveyData.propertyAge || null,
      bedrooms: surveyData.bedrooms || null,
      bathrooms: surveyData.bathrooms || null,
      floorAreaSqm: surveyData.floorAreaSqm || null,
      tenure: surveyData.tenure || null,
      listedStatus: surveyData.listedStatus || null,
      conservationArea: surveyData.conservationArea || false,
      parking: surveyData.parking || null,
      garden: surveyData.garden || null,
      latitude: surveyData.latitude || null,
      longitude: surveyData.longitude || null,
      geocodedAddress: surveyData.geocodedAddress || null,
      propertyResearch: surveyData.propertyResearch || null,
      researchCompletedAt: surveyData.researchCompletedAt || null,
      createdAt: surveyData.createdAt || now,
      updatedAt: surveyData.updatedAt || now
    }
    const id = await db.surveys.add(survey)
    return { ...survey, id }
  },

  async update(id, updates) {
    const updatedAt = new Date().toISOString()
    // Mark as needing sync if updating local data
    await db.surveys.update(id, { ...updates, updatedAt, synced: false })
    return db.surveys.get(id)
  },

  async markSynced(localId, remoteId) {
    await db.surveys.update(localId, { synced: true, remoteId })
    return db.surveys.get(localId)
  },

  async delete(id) {
    // Delete associated photos first
    await db.photos.where('surveyId').equals(id).delete()
    await db.surveys.delete(id)
  },

  async getAll() {
    return db.surveys.orderBy('createdAt').reverse().toArray()
  },

  async getById(id) {
    return db.surveys.get(id)
  },

  async getByRemoteId(remoteId) {
    return db.surveys.where('remoteId').equals(remoteId).first()
  },

  async getPending() {
    return db.surveys.filter(s => !s.synced).toArray()
  },

  async getPendingCount() {
    const pending = await db.surveys.filter(s => !s.synced).toArray()
    return pending.length
  },

  async getWithPhotos(id) {
    const survey = await db.surveys.get(id)
    if (!survey) return null
    const photos = await db.photos.where('surveyId').equals(id).toArray()
    return { ...survey, photos }
  }
}

// Helper functions for photos
export const photoDb = {
  async add(photoData) {
    const capturedAt = new Date().toISOString()
    const photo = {
      ...photoData,
      analyseWithAI: photoData.analyseWithAI !== false, // Default to true
      capturedAt,
      uploaded: false,
      r2Key: null
    }
    const id = await db.photos.add(photo)
    return { ...photo, id }
  },

  async delete(id) {
    await db.photos.delete(id)
  },

  async getBySurvey(surveyId) {
    return db.photos.where('surveyId').equals(surveyId).toArray()
  },

  async getByCategory(surveyId, categoryCode) {
    return db.photos
      .where('surveyId')
      .equals(surveyId)
      .filter(photo => photo.categoryCode === categoryCode)
      .toArray()
  },

  async updateNotes(id, notes) {
    await db.photos.update(id, { notes })
  },

  async updateAnalyseFlag(id, analyseWithAI) {
    await db.photos.update(id, { analyseWithAI })
  },

  async markUploaded(id, r2Key) {
    await db.photos.update(id, { uploaded: true, r2Key })
  }
}

// Helper functions for settings
export const settingsDb = {
  async get(key) {
    const setting = await db.settings.get(key)
    return setting?.value
  },

  async set(key, value) {
    await db.settings.put({ key, value })
  }
}

// Helper functions for activity logs
export const activityDb = {
  async log(surveyId, type, additionalData = {}) {
    const timestamp = new Date().toISOString()
    const log = {
      surveyId,
      type, // 'pdf_download', 'pdf_share', etc.
      timestamp,
      ...additionalData
    }
    const id = await db.activity_logs.add(log)
    return { ...log, id }
  },

  async getRecent(limit = 50) {
    return db.activity_logs
      .orderBy('timestamp')
      .reverse()
      .limit(limit)
      .toArray()
  },

  async getBySurvey(surveyId) {
    return db.activity_logs
      .where('surveyId')
      .equals(surveyId)
      .reverse()
      .sortBy('timestamp')
  },

  async clear() {
    await db.activity_logs.clear()
  }
}

export default db
